import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { toast } from 'sonner';
import {
  FileBadge, Landmark, Plus, X, ShieldAlert, AlertTriangle, CalendarClock,
  CheckCircle2, Search,
} from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const inp = 'w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]';
const lbl = 'block text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500 mb-1';
const TH = 'px-3 py-2 text-left text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';

// Expiry badge — same colour convention as compliance: green >60d, amber 15–60, rose <15, red expired
function ExpiryBadge({ bg }) {
  const d = bg.days_to_expiry;
  if (bg.status === 'released') return <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-slate-100 text-slate-500 border border-slate-200 rounded-sm">released</span>;
  if (bg.status === 'invoked') return <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-rose-600 text-white rounded-sm">invoked</span>;
  if (bg.effective_status === 'expired' || (d !== null && d < 0)) {
    return <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-red-600 text-white rounded-sm" data-testid={`bg-badge-expired-${bg.bg_no}`}>expired</span>;
  }
  if (d === null) return <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-slate-100 text-slate-500 rounded-sm">—</span>;
  const cls = d > 60 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : d >= 15 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase border rounded-sm ${cls}`} data-testid={`bg-badge-${bg.bg_no}`}>{d}d left</span>;
}

const KPI = ({ label, value, sub, tone = 'default', testId }) => {
  const tones = { default: 'border-slate-200', amber: 'border-amber-300 bg-amber-50', rose: 'border-rose-300 bg-rose-50', green: 'border-emerald-300 bg-emerald-50' };
  return (
    <div className={`border rounded-sm px-4 py-2.5 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="font-display text-lg font-extrabold text-[#3b4872]">{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
};

// ─── Vendor search (received BGs) with inline compliance status ─────────────
function VendorComplianceePicker({ value, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [compliance, setCompliance] = useState(null);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      apiGet('/vendors', { q, limit: 10 }).then((d) => { setResults((d?.items || d || []).slice(0, 10)); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setCompliance(null);
    if (!value?.id) return;
    apiGet(`/finance/bg-vendor-compliance/${value.id}`).then(setCompliance).catch(() => {});
  }, [value]);
  return (
    <div className="relative">
      <label className={lbl}>Counterparty Vendor (sub who gave the BG)</label>
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className={`${inp} pl-8`} placeholder={value?.name || 'Search vendor…'} value={q}
          onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
          data-testid="bg-vendor-search" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-56 overflow-auto">
          {results.map((v) => (
            <button key={v.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100"
              onClick={() => { onPick(v); setQ(''); setOpen(false); }} data-testid={`bg-vendor-option-${v.vendor_code || v.id}`}>
              <span className="font-bold">{v.name}</span><span className="text-[11px] text-slate-500 ml-2">{v.vendor_code}</span>
            </button>
          ))}
        </div>
      )}
      {value?.name && compliance && (
        compliance.ok ? (
          <div className="flex items-center gap-1 text-[11px] text-emerald-700 mt-1" data-testid="bg-vendor-compliance-ok">
            <CheckCircle2 size={12} /> {value.name} — compliance documents valid
          </div>
        ) : (
          <div className="flex items-center gap-1 text-[11px] text-rose-600 font-bold mt-1" data-testid="bg-vendor-compliance-bad">
            <ShieldAlert size={12} /> {value.name} — EXPIRED: {compliance.expired_docs.join(', ')}
          </div>
        )
      )}
    </div>
  );
}

// ─── BG create/edit drawer ───────────────────────────────────────────────────
function BGForm({ editing, projects, onClose, onSaved }) {
  const [f, setF] = useState(editing || {
    bg_no: '', direction: 'issued', bg_type: 'performance', issuing_bank: '',
    beneficiary: '', project_id: '', amount: '', margin_pct: 10,
    issue_date: new Date().toISOString().slice(0, 10), expiry_date: '',
    claim_expiry_date: '', charges_paid: 0, notes: '',
  });
  const [vendor, setVendor] = useState(editing?.counterparty_vendor_id ? { id: editing.counterparty_vendor_id, name: editing.counterparty_vendor_name } : null);
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const marginAmt = Number(f.amount || 0) * Number(f.margin_pct || 0) / 100;

  const save = async () => {
    if (!f.bg_no || !f.issuing_bank || !f.project_id || !f.expiry_date) return toast.error('BG No, bank, project and expiry are required');
    if (Number(f.amount) <= 0) return toast.error('Amount must be positive');
    const body = {
      ...f, amount: Number(f.amount), margin_pct: Number(f.margin_pct || 0),
      charges_paid: Number(f.charges_paid || 0),
      claim_expiry_date: f.claim_expiry_date || null,
      counterparty_vendor_id: f.direction === 'received' ? vendor?.id || null : null,
      documents: editing?.documents || [],
    };
    try {
      if (editing) await apiPut(`/finance/bank-guarantees/${editing.id}`, body);
      else await apiPost('/finance/bank-guarantees', body);
      toast.success(editing ? 'BG updated' : 'BG recorded');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid="bg-form-drawer">
      <div className="bg-white h-full w-[560px] max-w-full overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-extrabold">{editing ? `Edit ${editing.bg_no}` : 'Record Bank Guarantee'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bg-form-close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>BG Number</label><input className={inp} value={f.bg_no} onChange={(e) => u('bg_no', e.target.value)} data-testid="bg-form-no" /></div>
          <div><label className={lbl}>Direction</label>
            <select className={inp} value={f.direction} onChange={(e) => u('direction', e.target.value)} data-testid="bg-form-direction">
              <option value="issued">Issued (we gave to client)</option>
              <option value="received">Received (sub gave to us)</option>
            </select>
          </div>
          <div><label className={lbl}>Type</label>
            <select className={inp} value={f.bg_type} onChange={(e) => u('bg_type', e.target.value)} data-testid="bg-form-type">
              {['performance', 'advance', 'retention', 'bid', 'other'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Issuing Bank</label><input className={inp} value={f.issuing_bank} onChange={(e) => u('issuing_bank', e.target.value)} data-testid="bg-form-bank" /></div>
          <div className="col-span-2"><label className={lbl}>{f.direction === 'issued' ? 'Beneficiary (client)' : 'Beneficiary (us)'}</label><input className={inp} value={f.beneficiary} onChange={(e) => u('beneficiary', e.target.value)} data-testid="bg-form-beneficiary" /></div>
          {f.direction === 'received' && (
            <div className="col-span-2"><VendorComplianceePicker value={vendor} onPick={setVendor} /></div>
          )}
          <div className="col-span-2"><label className={lbl}>Project</label>
            <select className={inp} value={f.project_id} onChange={(e) => u('project_id', e.target.value)} data-testid="bg-form-project">
              <option value="">— select project —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Amount (₹)</label><input type="number" className={inp} value={f.amount} onChange={(e) => u('amount', e.target.value)} data-testid="bg-form-amount" /></div>
          <div><label className={lbl}>Margin % (cash blocked)</label>
            <input type="number" className={inp} value={f.margin_pct} onChange={(e) => u('margin_pct', e.target.value)} data-testid="bg-form-margin" />
            <div className="text-[10px] text-slate-500 mt-0.5" data-testid="bg-form-margin-amt">Margin blocked: {formatINR(marginAmt)}</div>
          </div>
          <div><label className={lbl}>Issue Date</label><input type="date" className={inp} value={f.issue_date} onChange={(e) => u('issue_date', e.target.value)} data-testid="bg-form-issue-date" /></div>
          <div><label className={lbl}>Validity Expiry</label><input type="date" className={inp} value={f.expiry_date} onChange={(e) => u('expiry_date', e.target.value)} data-testid="bg-form-expiry" /></div>
          <div><label className={lbl}>Claim Period End (optional)</label><input type="date" className={inp} value={f.claim_expiry_date || ''} onChange={(e) => u('claim_expiry_date', e.target.value)} data-testid="bg-form-claim-expiry" /></div>
          <div><label className={lbl}>BG Charges Paid (₹)</label><input type="number" className={inp} value={f.charges_paid} onChange={(e) => u('charges_paid', e.target.value)} data-testid="bg-form-charges" /></div>
          <div className="col-span-2"><label className={lbl}>Notes</label><input className={inp} value={f.notes} onChange={(e) => u('notes', e.target.value)} data-testid="bg-form-notes" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="px-4 py-2 text-xs font-display font-bold uppercase border border-slate-300 rounded-sm" onClick={onClose} data-testid="bg-form-cancel">Cancel</button>
          <button className="px-4 py-2 text-xs font-display font-bold uppercase text-white bg-[#3b4872] rounded-sm" onClick={save} data-testid="bg-form-save">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Facility form ───────────────────────────────────────────────────────────
function FacilityForm({ editing, banks, onClose, onSaved }) {
  const [f, setF] = useState(editing || { bank_name: '', facility_type: 'bg_limit', sanctioned_amount: '', linked_bank_account_id: '', valid_till: '', is_active: true });
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.bank_name || Number(f.sanctioned_amount) <= 0) return toast.error('Bank name and sanctioned amount required');
    const body = { ...f, sanctioned_amount: Number(f.sanctioned_amount), linked_bank_account_id: f.linked_bank_account_id || null, valid_till: f.valid_till || null };
    try {
      if (editing) await apiPut(`/finance/facilities/${editing.id}`, body);
      else await apiPost('/finance/facilities', body);
      toast.success('Facility saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid="facility-form-drawer">
      <div className="bg-white h-full w-[460px] max-w-full overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-extrabold">{editing ? 'Edit Facility' : 'Add Bank Facility'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div><label className={lbl}>Bank Name</label><input className={inp} value={f.bank_name} onChange={(e) => u('bank_name', e.target.value)} data-testid="facility-form-bank" /></div>
          <div><label className={lbl}>Facility Type</label>
            <select className={inp} value={f.facility_type} onChange={(e) => u('facility_type', e.target.value)} data-testid="facility-form-type">
              <option value="bg_limit">BG Limit</option><option value="od">OD</option><option value="cc">CC</option><option value="lc">LC</option>
            </select>
          </div>
          <div><label className={lbl}>Sanctioned Amount (₹)</label><input type="number" className={inp} value={f.sanctioned_amount} onChange={(e) => u('sanctioned_amount', e.target.value)} data-testid="facility-form-amount" /></div>
          {['od', 'cc'].includes(f.facility_type) && (
            <div><label className={lbl}>Linked Bank Account (F1)</label>
              <select className={inp} value={f.linked_bank_account_id || ''} onChange={(e) => u('linked_bank_account_id', e.target.value)} data-testid="facility-form-account">
                <option value="">— none —</option>
                {banks.map((b) => <option key={b.id} value={b.id}>{b.account_name}</option>)}
              </select>
            </div>
          )}
          <div><label className={lbl}>Valid Till</label><input type="date" className={inp} value={f.valid_till || ''} onChange={(e) => u('valid_till', e.target.value)} data-testid="facility-form-valid" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="px-4 py-2 text-xs font-display font-bold uppercase border border-slate-300 rounded-sm" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 text-xs font-display font-bold uppercase text-white bg-[#3b4872] rounded-sm" onClick={save} data-testid="facility-form-save">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function BGRegister() {
  const [tab, setTab] = useUrlTab('tab', 'guarantees');
  const [dash, setDash] = useState(null);
  const [bgs, setBgs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [banks, setBanks] = useState([]);
  const [dirF, setDirF] = useState('');
  const [typeF, setTypeF] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showFacForm, setShowFacForm] = useState(false);
  const [editingFac, setEditingFac] = useState(null);

  const load = useCallback(() => {
    apiGet('/finance/bg-dashboard').then(setDash).catch(() => {});
    apiGet('/finance/bank-guarantees', { ...(dirF ? { direction: dirF } : {}), ...(typeF ? { bg_type: typeF } : {}) })
      .then((d) => setBgs(d?.items || [])).catch(() => {});
  }, [dirF, typeF]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiGet('/projects').then((d) => setProjects(Array.isArray(d) ? d : [])).catch(() => {});
    apiGet('/finance/bank-accounts').then((d) => setBanks(d?.items || [])).catch(() => {});
  }, []);

  const act = async (bg, action) => {
    try {
      if (action === 'extend') {
        const newExpiry = window.prompt(`Extend ${bg.bg_no} — new validity expiry (YYYY-MM-DD):`, bg.expiry_date);
        if (!newExpiry) return;
        await apiPost(`/finance/bank-guarantees/${bg.id}/extend`, { new_expiry: newExpiry });
        toast.success(`${bg.bg_no} extended to ${newExpiry}`);
      } else if (action === 'release') {
        if (!window.confirm(`Release ${bg.bg_no}? It will stop counting towards facility utilisation.`)) return;
        await apiPost(`/finance/bank-guarantees/${bg.id}/release`, {});
        toast.success(`${bg.bg_no} released`);
      } else if (action === 'invoke') {
        const reason = window.prompt(`⚠ INVOKE ${bg.bg_no}? This records that the beneficiary ENCASHED the guarantee — a serious event.\n\nReason (required):`);
        if (!reason) return;
        await apiPost(`/finance/bank-guarantees/${bg.id}/invoke`, { reason });
        toast.error(`${bg.bg_no} marked INVOKED — surfaced on the dashboard`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
  };

  const t = dash?.totals || {};
  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="bg-register-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><FileBadge size={26} className="text-[#3b4872]" /> Bank Guarantees & Facilities</h1>
          <p className="text-sm text-slate-600 mt-1">BG register (issued + received) · live facility utilisation · expiry alerts (one reminder per BG per week).</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase text-white bg-[#3b4872] rounded-sm" data-testid="bg-add-btn">
          <Plus size={13} /> Record BG
        </button>
      </div>

      {/* INVOKED — board-level incident banner */}
      {(dash?.invoked || []).length > 0 && (
        <div className="border-2 border-rose-500 bg-rose-50 rounded-sm p-4" data-testid="bg-invoked-banner">
          <div className="flex items-center gap-2 font-display font-extrabold text-rose-700 text-sm uppercase tracking-wide">
            <ShieldAlert size={16} /> {dash.invoked.length} Bank Guarantee(s) INVOKED — review immediately
          </div>
          {dash.invoked.map((b) => (
            <div key={b.id} className="mt-2 text-sm text-rose-800" data-testid={`bg-invoked-${b.bg_no}`}>
              <b>{b.bg_no}</b> · {b.bg_type} · {formatINR(b.amount)} · {b.project_name} — “{b.invoked_reason}” <span className="text-xs">({b.invoked_by}, {formatDate(b.invoked_at)})</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Active Issued BGs" value={formatINR(t.active_issued_value)} sub={`${t.active_issued_count || 0} guarantees`} testId="kpi-issued" />
        <KPI label="Margin Money Blocked" value={formatINR(t.margin_blocked)} tone="amber" testId="kpi-margin" />
        <KPI label="Expiring ≤ 60 days" value={`${dash?.expiring?.d60?.count || 0} · ${formatINR(dash?.expiring?.d60?.value)}`} tone={dash?.expiring?.d60?.count ? 'rose' : 'default'} testId="kpi-expiring" />
        <KPI label="Facility Headroom" value={formatINR(dash?.facility_headroom_total)} tone="green" testId="kpi-headroom" />
      </div>

      <div className="flex border-b border-slate-200">
        {[{ id: 'guarantees', label: 'Guarantees', icon: FileBadge }, { id: 'facilities', label: 'Facilities', icon: Landmark }].map((x) => (
          <button key={x.id} onClick={() => setTab(x.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px ${tab === x.id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${x.id}`}>
            <x.icon size={14} /> {x.label}
          </button>
        ))}
      </div>

      {tab === 'guarantees' && (
        <div>
          <div className="flex gap-2 mb-3">
            <select className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs" value={dirF} onChange={(e) => setDirF(e.target.value)} data-testid="bg-dir-filter">
              <option value="">All directions</option><option value="issued">Issued</option><option value="received">Received</option>
            </select>
            <select className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs" value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="bg-type-filter">
              <option value="">All types</option>
              {['performance', 'advance', 'retention', 'bid', 'other'].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-sm">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr><th className={TH}>BG No</th><th className={TH}>Dir / Type</th><th className={TH}>Bank</th><th className={TH}>Beneficiary</th><th className={TH}>Project</th><th className="px-3 py-2 text-right text-[10px] font-display font-bold uppercase text-slate-500">Amount</th><th className="px-3 py-2 text-right text-[10px] font-display font-bold uppercase text-slate-500">Margin</th><th className={TH}>Expiry</th><th className={TH}>Actions</th></tr>
              </thead>
              <tbody>
                {bgs.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-500" data-testid="bg-empty">No bank guarantees recorded.</td></tr>}
                {bgs.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bg-row-${b.bg_no}`}>
                    <td className="px-3 py-2 text-sm font-bold cursor-pointer" onClick={() => { setEditing(b); setShowForm(true); }}>{b.bg_no}
                      {b.extensions?.length > 0 && <span className="ml-1.5 text-[9px] text-slate-400">+{b.extensions.length} ext</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">{b.direction} · {b.bg_type}</td>
                    <td className="px-3 py-2 text-xs">{b.issuing_bank}</td>
                    <td className="px-3 py-2 text-xs">{b.beneficiary || b.counterparty_vendor_name || '—'}</td>
                    <td className="px-3 py-2 text-xs">{b.project_name}</td>
                    <td className="px-3 py-2 text-sm text-right font-display font-bold">{formatINR(b.amount)}</td>
                    <td className="px-3 py-2 text-xs text-right">{formatINR(b.margin_amount)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <span><ExpiryBadge bg={b} /> <span className="text-[10px] text-slate-500 ml-1">{formatDate(b.expiry_date)}</span></span>
                        {b.claim_open && (
                          <span className="flex items-center gap-1 text-[10px] text-amber-700 font-bold" data-testid={`bg-claim-open-${b.bg_no}`}>
                            <CalendarClock size={10} /> claim period open till {formatDate(b.claim_expiry_date)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {!['released', 'invoked'].includes(b.status) && (<>
                        <button onClick={() => act(b, 'extend')} className="text-[10px] font-bold text-[#3b4872] hover:underline mr-2" data-testid={`bg-extend-${b.bg_no}`}>Extend</button>
                        <button onClick={() => act(b, 'release')} className="text-[10px] font-bold text-emerald-600 hover:underline mr-2" data-testid={`bg-release-${b.bg_no}`}>Release</button>
                        <button onClick={() => act(b, 'invoke')} className="text-[10px] font-bold text-rose-600 hover:underline" data-testid={`bg-invoke-${b.bg_no}`}>Invoke</button>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'facilities' && (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => { setEditingFac(null); setShowFacForm(true); }} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase text-white bg-[#3b4872] rounded-sm" data-testid="facility-add-btn">
              <Plus size={13} /> Add Facility
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(dash?.facilities || []).length === 0 && <div className="col-span-3 text-center py-10 text-slate-500" data-testid="facility-empty">No facilities configured. Add your sanctioned BG/OD/CC limits.</div>}
            {(dash?.facilities || []).map((f) => {
              const pct = f.sanctioned_amount ? Math.min(100, (f.utilised / f.sanctioned_amount) * 100) : 0;
              return (
                <div key={f.id} className="border border-slate-300 rounded-sm p-4" data-testid={`facility-card-${f.bank_name}-${f.facility_type}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-display font-extrabold text-sm flex items-center gap-1.5"><Landmark size={14} className="text-[#3b4872]" /> {f.bank_name}</div>
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-slate-100 text-slate-600 rounded-sm">{f.facility_type}</span>
                  </div>
                  <div className="mt-3 h-3 bg-slate-100 rounded-sm overflow-hidden border border-slate-200">
                    <div className={`h-full ${pct > 90 ? 'bg-rose-500' : pct > 70 ? 'bg-amber-400' : 'bg-[#3b4872]'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex justify-between mt-2 text-xs">
                    <div><div className="text-[10px] uppercase font-bold text-slate-400">Utilised (live)</div><div className="font-display font-bold" data-testid={`facility-utilised-${f.bank_name}-${f.facility_type}`}>{formatINR(f.utilised)}</div></div>
                    <div className="text-right"><div className="text-[10px] uppercase font-bold text-slate-400">Headroom</div><div className="font-display font-bold text-emerald-700">{formatINR(f.headroom)}</div></div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">Sanctioned {formatINR(f.sanctioned_amount)}{f.valid_till ? ` · valid till ${formatDate(f.valid_till)}` : ''}</div>
                  <button onClick={() => { setEditingFac(f); setShowFacForm(true); }} className="text-[11px] font-bold text-slate-500 hover:underline mt-2" data-testid={`facility-edit-${f.bank_name}-${f.facility_type}`}>Edit</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showForm && <BGForm editing={editing} projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {showFacForm && <FacilityForm editing={editingFac} banks={banks} onClose={() => setShowFacForm(false)} onSaved={() => { setShowFacForm(false); load(); }} />}
    </div>
  );
}
